import React, { FC, useState } from 'react'
import { BaseFacetProps } from '../../types'
import styles from '../../Facets.module.scss'
import InputLabel from 'components/SA/FormElements/InputLabel'
import { SizeFacetConnectorProps } from 'connectors/Facets'
import { validateState } from '../../helpers/sizeFacet'
import { updateClientSideUrl } from '../../helpers/url'

export type DimensionsObject = {
  currentSize: string
  heightMax: number | null | undefined
  heightMin: number | null | undefined
  heightError: boolean
  widthMax: number | null | undefined
  widthMin: number | null | undefined
  widthError: boolean
}

const initialDimensionsState: DimensionsObject = {
  currentSize: 'custom',
  heightMax: null,
  heightMin: null,
  heightError: false,
  widthMax: null,
  widthMin: null,
  widthError: false,
}

type CustomSizeFieldsProps = BaseFacetProps & SizeFacetConnectorProps

const CustomSizeFields: FC<CustomSizeFieldsProps> = ({
  baseUrl,
  nextAppRoute,
  measurementUnit,
  sizeMinMax,
  getHref,
}) => {
  let timer
  const timerDuration = 1500

  const [isCustomSizeOpen, setIsCustomSizeOpen] = useState(false)
  const [dimensionsState, setDimensionsState] = useState({
    ...initialDimensionsState,
    ...sizeMinMax,
  })

  const { heightMin, heightMax, widthMin, widthMax, widthError, heightError } = dimensionsState

  const handleCustomSizeToggle = () => {
    const { heightMax, heightMin, widthMax, widthMin } = dimensionsState
    const shouldResetValue = !!heightMax || !!heightMin || !!widthMax || !!widthMin
    if (isCustomSizeOpen && shouldResetValue) {
      setDimensionsState(initialDimensionsState)
      const href = getHref({
        size: '',
        width: '',
        height: '',
        page: '', // Reset Page
      })
      const sizeHref = `${baseUrl}${href}`
      updateClientSideUrl(sizeHref, nextAppRoute)
    }
    setIsCustomSizeOpen((prevValue) => !prevValue)
  }

  const validateNumber = (value: string): number => {
    const validNumber = Number(value)
    return isNaN(validNumber) || validNumber < 0 ? 0 : validNumber
  }

  const inputUpdate = (data) => {
    clearTimeout(timer)
    const newState = { ...dimensionsState, ...data, currentSize: 'custom' }
    const errors = validateState(newState)
    setDimensionsState({ ...newState, ...errors })

    if (!errors.widthError && !errors.heightError) {
      timer = setTimeout(() => {
        if (window && window.dataLayer) {
          window.dataLayer.push({
            event: 'filter',
            label: `Size - Custom - Width ${newState.widthMin || ''}-${newState.widthMax ||
              ''} X Height ${newState.heightMin || ''}-${newState.heightMax || ''}`,
          })
        }

        const href = getHref({
          size: '',
          width:
            (newState.widthMin || 0) >= 0 && newState.widthMax
              ? `${newState.widthMin || 0}-${newState.widthMax}`
              : '',
          height:
            (newState.heightMin || 0) >= 0 && newState.heightMax
              ? `${newState.heightMin || 0}-${newState.heightMax}`
              : '',
          page: '', // Reset Page
        })
        const sizeHref = `${baseUrl}${href}`
        updateClientSideUrl(sizeHref, nextAppRoute)
      }, timerDuration)
    }

    return timer
  }

  const handleSizeError = (widthOrHeight: 'width' | 'height') => {
    if (widthOrHeight === 'width') {
      const isWidthMinNotEmpty = widthMin
      return widthError && isWidthMinNotEmpty && widthMax
    } else {
      const isHeightMinNotEmpty = heightMin
      return heightError && isHeightMinNotEmpty && heightMax
    }
  }

  return (
    <div className={styles.customSizeContainer}>
      <button onClick={handleCustomSizeToggle}>
        {isCustomSizeOpen ? 'Remove Custom Size' : 'Select Custom Size'}
      </button>
      {isCustomSizeOpen && (
        <div className={styles.sizeInputWrapper}>
          <div className={styles.sizeInputContainer}>
            <InputLabel
              type='text'
              name='wMin'
              value={widthMin || widthMin === 0 ? widthMin : ''}
              title='W Min'
              onChange={(ev) =>
                inputUpdate({
                  widthMin: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('width') ? ' ' : undefined}
            />
            {'-'}
            <InputLabel
              type='text'
              name='wMax'
              value={widthMax || ''}
              title='W Max'
              onChange={(ev) =>
                inputUpdate({
                  widthMax: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('width') ? ' ' : undefined}
            />
            {measurementUnit}
          </div>
          <div className={styles.sizeInputContainer}>
            <InputLabel
              type='text'
              name='hMin'
              value={heightMin || heightMin === 0 ? heightMin : ''}
              title='H Min'
              onChange={(ev) =>
                inputUpdate({
                  heightMin: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('height') ? ' ' : undefined}
            />
            {'-'}
            <InputLabel
              type='text'
              name='hMax'
              value={heightMax || ''}
              title='H Max'
              onChange={(ev) =>
                inputUpdate({
                  heightMax: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('height') ? ' ' : undefined}
            />
            {measurementUnit}
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomSizeFields
